import { ComponentStyleConfig } from '@chakra-ui/react'
import { Dict } from '@chakra-ui/utils'
import { mode } from '@chakra-ui/theme-tools'
import { AppPlatform } from '@rw1/models'
import { DISABLED_OPACITY } from './constants'
import {
  rw1MainBackgroundImage,
  rw1MainBackgroundImageAction,
} from './getRw1MainBackgroundImage'

const commonHeaderMenuStyles = (props: Dict) => ({
  // Remove the ugly blue outline.
  _focus: {
    boxShadow: 'none',
    outline: 0,
  },
  border: 'none',
  borderRadius: '0.5em',
  color: mode('rw1.text.light.6', 'rw1.colors.dark.white')(props),
})

const secondaryBackgroundColor = (props: Dict) => ({
  backgroundColor: mode('rw1.secondary.light.0', 'rw1.secondary.dark.0')(props),
})

const headerMenuHoverStyles = (props: Dict) => ({
  backgroundColor: mode(PLATFORM_API.app.getPlatform() == AppPlatform.Android ? 'rw1.secondary.light.1' : 'rw1.secondary.light.0', 'rw1.secondary.dark.0')(props),
})

const secondaryHeaderMenuHoverStyles = (props: Dict) => ({
  backgroundColor: mode('rw1.secondary.light.1', 'rw1.secondary.dark.1')(props),
})

const headerTableHoverStyles = (props: Dict) => ({
  backgroundColor: mode('rw1.secondary.light.1', 'rw1.secondary.dark.1')(props),
  color: mode('rw1.text.light.6', 'rw1.text.dark.6')(props),
})

const authBaseStyle = (props: Dict) => ({
  color: mode('rw1.colors.light.white', 'rw1.colors.dark.white')(props),
  backgroundColor: mode('rw1.primary.light.5', 'rw1.primary.dark.5')(props),
})

const activePrimary = (props: Dict) => ({
  ...rw1MainBackgroundImageAction(props),
  _disabled: {
    ...rw1MainBackgroundImage(props),
  },
})

const activeSecondary = (props: Dict) => ({
  ...secondaryBackgroundColor(props),

  color: mode('rw1.primary.light.6', 'rw1.primary.dark.6')(props),
  _disabled: {
    ...secondaryBackgroundColor(props),
  },
})

const activeTertiaty = (props: Dict) => ({
  textDecoration: 'underline',
  color: mode('rw1.primary.light.6', 'rw1.primary.dark.6')(props),
})

export const Button: ComponentStyleConfig = {
  baseStyle: {
    _disabled: {
      opacity: DISABLED_OPACITY,
    },
    letterSpacing: '0.02em',
    fontWeight: 600,
    borderRadius: 16,
  },
  sizes: {
    md: {
      borderRadius: 8,
    },
    headerMenu: {
      height: 12,
      minWidth: 12,
    },
    lg: {
      minHeight: 14,
      borderRadius: 16,
    },
  },
  variants: {
    primary: (props: Dict) => ({
      _hover: {
        ...activePrimary(props),
      },
      _active: {
        ...activePrimary(props),
      },
      _focus: { boxShadow: 'none', outline: 0 },
      backgroundSize: '100% 240%',
      backgroundPosition: 'bottom',
      transition: 'background-position 100ms ease-in',
      color: mode('rw1.colors.light.white', 'rw1.colors.dark.white')(props),
      ...rw1MainBackgroundImage(props),
    }),

    community: (props: Dict) => ({
      _focus: { boxShadow: 'rgba(100, 100, 111, 0.2) 0px 0px 10px 0px;' },
      boxShadow: 'rgba(100, 100, 111, 0.2) 0px 0px 10px 0px;',
      border: 'none',
      height: 44,
      bgPosition: 'center',
      bgRepeat: 'no-repeat',
      bgSize: 'contain',
      width: 'full',
      borderRadius: 16,
      color: mode('rw1.primary.light.5', 'rw1.primary.dark.3')(props),
    }),
    company: (props: Dict) => ({
      _focus: { boxShadow: 'rgba(100, 100, 111, 0.2) 0px 0px 10px 0px;' },
      boxShadow: 'rgba(100, 100, 111, 0.2) 0px 0px 10px 0px;',
      border: 'none',
      height: 44,
      width: 'full',
      borderRadius: 16,
      color: mode('rw1.primary.light.5', 'rw1.primary.dark.3')(props),
    }),
    companySelected: (props: Dict) => ({
      _focus: { boxShadow: 'none' },
      borderBottomRadius: '0',
      boxShadow: 'none',
      height: 44,
      width: 'full',
      borderRadius: 16,
      color: mode('rw1.primary.light.5', 'rw1.primary.dark.3')(props),
    }),

    secondary: (props: Dict) => ({
      _hover: {
        ...activeSecondary(props),
      },
      _active: {
        ...activeSecondary(props),
      },
      color: mode('rw1.primary.light.5', 'rw1.primary.dark.5')(props),
      ...secondaryBackgroundColor(props),
    }),

    tertiary: (props: Dict) => ({
      _hover: {
        ...activeTertiaty(props),
      },
      _active: {
        ...activeTertiaty(props),
      },
      _focus: {
        boxShadow: 'none',
        outline: 0,
      },
      border: 'none',
      color: mode('rw1.primary.light.5', 'rw1.primary.dark.5')(props),
    }),

    headerMenu: (props: Dict) => ({
      _active: {
        ...headerMenuHoverStyles(props),
      },
      _hover: {
        ...headerMenuHoverStyles(props),
      },
      ...commonHeaderMenuStyles(props),
    }),

    secondaryHeaderMenu: (props: Dict) => ({
      _active: {
        ...secondaryHeaderMenuHoverStyles(props),
      },
      _hover: {
        ...secondaryHeaderMenuHoverStyles(props),
      },
      ...commonHeaderMenuStyles(props),
    }),

    headerTable: (props: Dict) => ({
      color: mode('rw1.text.light.0', 'rw1.text.dark.0')(props),
      _active: {
        ...headerTableHoverStyles(props),
      },
      _hover: {
        ...headerTableHoverStyles(props),
      },
      _focus: {
        boxShadow: 'none',
        outline: 0,
      },
    }),

    auth: (props: Dict) => ({
      ...authBaseStyle(props),
      _hover: {
        _disabled: {
          ...authBaseStyle(props),
        },
      },
    }),

    text: (props: Dict) => ({
      _hover: {
        color: mode('rw1.text.light.1', 'rw1.text.dark.1')(props),
      },

      color: mode('rw1.text.light.5', 'rw1.text.dark.5')(props),
      textDecoration: 'underline',
    }),
    notifications: (props: Dict) => ({
      _hover: { textDecoration: 'underline', color: mode('rw1.primary.light.6', 'rw1.primary.dark.6')(props) },
      _active: { textDecoration: 'underline' },
      border: 'none',
      color: mode('rw1.primary.light.5', 'rw1.primary.dark.5')(props),
      marginInlineStart: 2,
      marginInlineEnd: 2,
      paddingInlineStart: 2,
      paddingInlineEnd: 2,
    }),
    downloadTable: (props: Dict) => ({
      _hover: {
        textDecoration: 'underline'
      },
      color: mode('rw1.primary.light.5', 'rw1.primary.dark.5')(props),
    }),
  },
  defaultProps: {
    variant: 'primary',
  },
}
